import { useEffect, useState } from "react";
import { Link, Navigate } from "react-router-dom";
import { ArrowLeft, Film, LogOut, ShieldCheck } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { useT } from "@/i18n/LanguageContext";
import { WhatsAppCta } from "@/components/WhatsAppContact";

const serif = { fontFamily: "'Cormorant Garamond', Georgia, serif" };
const API = process.env.REACT_APP_BACKEND_URL;
const pad3 = (n) => `#${String(n).padStart(3, "0")}`;

function FoundersPrivacyCard() {
  const { t } = useT();
  const [me, setMe] = useState(null);
  const [saved, setSaved] = useState(false);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const res = await fetch(`${API}/api/founders/me`, { credentials: "include" });
        if (!res.ok) return;
        const json = await res.json();
        if (alive) setMe(json);
      } catch (_) { /* silent */ }
    })();
    return () => { alive = false; };
  }, []);

  if (!me?.is_founder) return null;

  const setDisplay = async (display) => {
    setBusy(true);
    setSaved(false);
    try {
      const res = await fetch(`${API}/api/founders/me`, {
        method: "PATCH",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ display }),
      });
      if (res.ok) {
        setMe(await res.json());
        setSaved(true);
      }
    } catch (_) { /* silent */ }
    setBusy(false);
  };

  return (
    <div className="border border-white/10 bg-[#0b0b0b] p-6 md:p-7 mb-5" data-testid="settings-founders-card">
      <p className="text-sm text-bone mb-1 flex items-center gap-2">
        <ShieldCheck size={15} strokeWidth={1.5} className="text-gold" /> {t("my.fw.title")}
      </p>
      <p className="text-[11px] tracking-[0.22em] uppercase text-gold/80 mt-2" data-testid="settings-founders-number">
        {me.founder_number ? t("my.fw.badge", { number: pad3(me.founder_number) }) : "Founding Member"}
      </p>
      <p className="text-xs text-bone/45 mt-3 leading-relaxed">{t("my.fw.desc")}</p>

      <div className="mt-5 space-y-3">
        <label data-testid="settings-founders-yes" className="flex items-start gap-3 cursor-pointer">
          <input
            type="radio"
            name="settings-fw"
            disabled={busy}
            checked={me.display === "yes"}
            onChange={() => setDisplay("yes")}
            className="mt-1 accent-[#C5A059]"
          />
          <span className="text-sm text-bone/80 leading-relaxed">
            {t("join.fw.yes")}
            {me.display_preview ? <span className="block mt-0.5 text-gold/70 text-xs">→ {me.display_preview}</span> : null}
          </span>
        </label>
        <label data-testid="settings-founders-no" className="flex items-start gap-3 cursor-pointer">
          <input
            type="radio"
            name="settings-fw"
            disabled={busy}
            checked={me.display === "no"}
            onChange={() => setDisplay("no")}
            className="mt-1 accent-[#C5A059]"
          />
          <span className="text-sm text-bone/80 leading-relaxed">{t("join.fw.no")}</span>
        </label>
      </div>
      {saved && (
        <p data-testid="settings-founders-saved" className="mt-4 text-xs text-gold">{t("my.fw.saved")}</p>
      )}
    </div>
  );
}

export default function StoicSettings() {
  const { user, loading, logout } = useAuth();
  const { t } = useT();

  if (loading) return <main className="min-h-screen bg-[#050505]" />;
  if (!user) return <Navigate to="/my-stoicos" replace />;

  return (
    <main data-testid="stoicos-settings" className="min-h-screen bg-[#050505] pt-32 pb-24 px-6">
      <div className="max-w-3xl mx-auto">
        <Link
          data-testid="settings-back-link"
          to="/my-stoicos"
          className="inline-flex items-center gap-2 text-[11px] tracking-[0.2em] uppercase text-bone/40 hover:text-gold transition-colors mb-8"
        >
          <ArrowLeft size={13} strokeWidth={1.5} /> My StoicOs
        </Link>
        <p className="text-[11px] tracking-[0.28em] uppercase text-gold mb-4">My StoicOs</p>
        <h1 style={serif} className="text-4xl sm:text-5xl text-bone font-medium tracking-tight mb-10">
          {t("my.module.settings")}
        </h1>

        <div className="border border-white/10 bg-[#0b0b0b] p-6 md:p-7 mb-5 flex flex-wrap items-center justify-between gap-4">
          <div>
            <p className="text-sm text-bone mb-1 flex items-center gap-2">
              <Film size={15} strokeWidth={1.5} className="text-gold" /> {t("my.replayIntro")}
            </p>
            <p className="text-xs text-bone/45">{t("my.settings.replayDesc")}</p>
          </div>
          <Link
            data-testid="replay-intro-button"
            to="/my-stoicos/onboarding/1?replay=1"
            className="inline-flex items-center text-[11px] tracking-[0.24em] uppercase border border-gold/50 text-gold hover:bg-gold hover:text-ink px-6 py-3 transition-colors"
          >
            {t("my.replayIntro")}
          </Link>
        </div>

        {/* Privacy — Founders Wall of Honor */}
        <FoundersPrivacyCard />

        {/* Support — WhatsApp contact */}
        <div className="border border-white/10 bg-[#0b0b0b] p-6 md:p-7 mb-5 flex flex-wrap items-center justify-between gap-4" data-testid="settings-whatsapp-card">
          <div>
            <p className="text-sm text-bone mb-1">Need help?</p>
            <p className="text-xs text-bone/45 max-w-sm leading-relaxed">
              Questions about your account, membership, Academy access or Virtue Guide?
            </p>
          </div>
          <WhatsAppCta source="mystoicos_support">
            Message us on WhatsApp
          </WhatsAppCta>
        </div>

        <div className="border border-white/10 bg-[#0b0b0b] p-6 md:p-7 flex flex-wrap items-center justify-between gap-4">
          <p className="text-sm text-bone/70">{user.email}</p>
          <button
            data-testid="settings-logout-button"
            onClick={logout}
            className="inline-flex items-center gap-2 text-[11px] tracking-[0.2em] uppercase text-bone/40 hover:text-gold transition-colors"
          >
            <LogOut size={13} strokeWidth={1.5} /> {t("my.logout")}
          </button>
        </div>
      </div>
    </main>
  );
}
